import { useEffect, useId, useRef } from 'react';

interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  isLoading?: boolean;
  variant?: 'danger' | 'success' | 'warning' | 'info';
  /**
   * A second way forward, between Cancel and the main action, for questions
   * with two real answers ("Delete it" / "Keep it as it was"). Cancel and
   * Escape still mean "do neither".
   */
  secondaryText?: string;
  onSecondary?: () => void;
}

/**
 * A yes/no question over whatever is on screen.
 *
 * It behaves as a modal for the keyboard too: focus moves to Cancel when it
 * opens (the answer that changes nothing), Tab stays on its two buttons,
 * Escape cancels, and focus goes back where it was when it closes. Without
 * that, Tab walked on through the screen behind it — to buttons the prompt
 * was meant to stand in front of — and Escape reached whatever was beneath.
 */
export const ConfirmModal = (props: ConfirmModalProps) =>
  props.isOpen ? <ConfirmDialog {...props} /> : null;

const ConfirmDialog = ({
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  isLoading = false,
  variant = 'info',
  secondaryText,
  onSecondary,
}: ConfirmModalProps) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const messageId = useId();

  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    cancelRef.current?.focus();
    return () => {
      if (opener?.isConnected) opener.focus();
    };
  }, []);

  // On the document, in the capture phase: ahead of any listener of the
  // screen behind (a drawer's own Escape, say), and whether or not focus is
  // still inside — a click on the backdrop takes it away.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const isLoadingRef = useRef(isLoading);
  isLoadingRef.current = isLoading;
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        event.preventDefault();
        if (!isLoadingRef.current) onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;
      event.stopPropagation();
      const buttons = Array.from(
        dialogRef.current.querySelectorAll<HTMLButtonElement>('button:not([disabled])'),
      );
      if (buttons.length === 0) {
        event.preventDefault();
        return;
      }
      const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
      const next = event.shiftKey
        ? (index <= 0 ? buttons.length : index) - 1
        : (index + 1) % buttons.length;
      event.preventDefault();
      buttons[next].focus();
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, []);

  const variantStyles = {
    danger: {
      icon: '⚠️',
      iconBg: 'bg-red-100',
      iconColor: 'text-red-600',
      buttonBg: 'bg-red-600 hover:bg-red-700',
      buttonDisabled: 'bg-red-400',
    },
    success: {
      icon: '✓',
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600',
      buttonBg: 'bg-green-600 hover:bg-green-700',
      buttonDisabled: 'bg-green-400',
    },
    warning: {
      icon: '⚡',
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-600',
      buttonBg: 'bg-orange-600 hover:bg-orange-700',
      buttonDisabled: 'bg-orange-400',
    },
    info: {
      icon: 'ℹ️',
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      buttonBg: 'bg-blue-600 hover:bg-blue-700',
      buttonDisabled: 'bg-blue-400',
    },
  };

  const styles = variantStyles[variant];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[100]">
      <div
        ref={dialogRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={messageId}
        className="bg-white rounded-xl border border-slate-200 max-w-md w-full shadow-xl"
      >
        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Icon */}
          <div className="flex items-center justify-center">
            <div className={`${styles.iconBg} rounded-full p-3`}>
              <div className={`text-3xl ${styles.iconColor}`}>{styles.icon}</div>
            </div>
          </div>

          {/* Title and Message */}
          <div className="text-center space-y-2">
            <h2 id={titleId} className="text-xl font-bold text-slate-900">
              {title}
            </h2>
            <div id={messageId} className="text-slate-600">
              {message}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 p-6 pt-0">
          <button
            ref={cancelRef}
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed"
          >
            {cancelText}
          </button>
          {secondaryText && onSecondary && (
            <button
              type="button"
              onClick={onSecondary}
              disabled={isLoading}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:text-slate-400 disabled:cursor-not-allowed"
            >
              {secondaryText}
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              onConfirm();
            }}
            disabled={isLoading}
            className={`flex-1 px-4 py-2.5 text-sm font-medium text-white rounded-lg transition-colors disabled:cursor-not-allowed ${
              isLoading ? styles.buttonDisabled : styles.buttonBg
            }`}
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Processing...
              </span>
            ) : (
              confirmText
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
